import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-cta text-white hover:bg-primary/90 shadow-feature hover:shadow-floating hover:scale-105",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-feature hover:shadow-floating",
        outline:
          "border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-card hover:shadow-feature hover:scale-105",
        secondary:
          "bg-gradient-accent text-secondary-foreground hover:bg-secondary/80 shadow-feature hover:shadow-floating",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:scale-105",
        link: "text-primary underline-offset-4 hover:underline",
        gradient: "bg-gradient-cta text-white hover:bg-primary/90 shadow-floating hover:shadow-feature hover:scale-105 border border-white/20",
        glass: "bg-background/80 backdrop-blur-xl border border-white/20 text-foreground hover:bg-background hover:shadow-feature hover:scale-105",
        "primary-cta": "bg-cta-primary text-cta-primary-foreground hover:bg-cta-primary-hover shadow-lg hover:shadow-xl hover:scale-105 font-bold transition-all duration-200",
      },
      size: {
        default: "h-12 px-6 py-3",
        sm: "h-10 rounded-lg px-4",
        lg: "h-14 rounded-xl px-8 text-lg",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
